import {
  DuplicateClientException,
  ExceptionWithMessage,
} from 'shared/helpers/CustomErrors';
import types from '../../mutation-types';
import ClientAPI from '../../../api/clients';
import snakecaseKeys from 'snakecase-keys';
import AccountActionsAPI from '../../../api/accountActions';
import AnalyticsHelper from '../../../helper/AnalyticsHelper';
import { CLIENTS_EVENTS } from '../../../helper/AnalyticsHelper/events';

const buildClientFormData = clientParams => {
  const formData = new FormData();
  const { additional_attributes = {}, ...clientProperties } = clientParams;
  Object.keys(clientProperties).forEach(key => {
    if (clientProperties[key]) {
      formData.append(key, clientProperties[key]);
    }
  });
  const { social_profiles, ...additionalAttributesProperties } =
    additional_attributes;
  Object.keys(additionalAttributesProperties).forEach(key => {
    formData.append(
      `additional_attributes[${key}]`,
      additionalAttributesProperties[key]
    );
  });
  Object.keys(social_profiles).forEach(key => {
    formData.append(
      `additional_attributes[social_profiles][${key}]`,
      social_profiles[key]
    );
  });
  return formData;
};

export const handleClientOperationErrors = error => {
  if (error.response?.status === 422) {
    throw new DuplicateClientException(error.response.data.attributes);
  } else if (error.response?.data?.message) {
    throw new ExceptionWithMessage(error.response.data.message);
  } else {
    throw new Error(error);
  }
};

export const actions = {
  search: async ({ commit }, { search, page, sortAttr, label }) => {
    commit(types.SET_CLIENT_UI_FLAG, { isFetching: true });
    try {
      const {
        data: { payload, meta },
      } = await ClientAPI.search(search, page, sortAttr, label);
      commit(types.CLEAR_CLIENTS);
      commit(types.SET_CLIENTS, payload);
      commit(types.SET_CLIENT_META, meta);
      commit(types.SET_CLIENT_UI_FLAG, { isFetching: false });
    } catch (error) {
      commit(types.SET_CLIENT_UI_FLAG, { isFetching: false });
    }
  },

  get: async ({ commit }, { page = 1, sortAttr, label } = {}) => {
    commit(types.SET_CLIENT_UI_FLAG, { isFetching: true });
    try {
      const {
        data: { payload, meta },
      } = await ClientAPI.get(page, sortAttr, label);
      commit(types.CLEAR_CLIENTS);
      commit(types.SET_CLIENTS, payload);
      commit(types.SET_CLIENT_META, meta);
      commit(types.SET_CLIENT_UI_FLAG, { isFetching: false });
    } catch (error) {
      commit(types.SET_CLIENT_UI_FLAG, { isFetching: false });
    }
  },

  active: async ({ commit }, { page = 1, sortAttr } = {}) => {
    commit(types.SET_CLIENT_UI_FLAG, { isFetching: true });
    try {
      const {
        data: { payload, meta },
      } = await ClientAPI.active(page, sortAttr);
      commit(types.CLEAR_CLIENTS);
      commit(types.SET_CLIENTS, payload);
      commit(types.SET_CLIENT_META, meta);
      commit(types.SET_CLIENT_UI_FLAG, { isFetching: false });
    } catch (error) {
      commit(types.SET_CLIENT_UI_FLAG, { isFetching: false });
    }
  },

  show: async ({ commit }, { id }) => {
    commit(types.SET_CLIENT_UI_FLAG, { isFetchingItem: true });
    try {
      const response = await ClientAPI.show(id);
      commit(types.SET_CLIENT_ITEM, response.data.payload);
      commit(types.SET_CLIENT_UI_FLAG, {
        isFetchingItem: false,
      });
    } catch (error) {
      commit(types.SET_CLIENT_UI_FLAG, {
        isFetchingItem: false,
      });
    }
  },

  update: async ({ commit }, { id, isFormData = false, ...clientParams }) => {
    const { avatar, customAttributes, ...paramsToDecamelize } = clientParams;
    const decamelizedClientParams = {
      ...snakecaseKeys(paramsToDecamelize, { deep: true }),
      ...(customAttributes && { custom_attributes: customAttributes }),
      ...(avatar && { avatar }),
    };
    commit(types.SET_CLIENT_UI_FLAG, { isUpdating: true });
    try {
      const response = await ClientAPI.update(
        id,
        isFormData
          ? buildClientFormData(decamelizedClientParams)
          : decamelizedClientParams
      );
      commit(types.EDIT_CLIENT, response.data.payload);
      commit(types.SET_CLIENT_UI_FLAG, { isUpdating: false });
    } catch (error) {
      commit(types.SET_CLIENT_UI_FLAG, { isUpdating: false });
      handleClientOperationErrors(error);
    }
  },

  create: async ({ commit }, { isFormData = false, ...clientParams }) => {
    const decamelizedClientParams = snakecaseKeys(clientParams, {
      deep: true,
    });
    commit(types.SET_CLIENT_UI_FLAG, { isCreating: true });
    try {
      const response = await ClientAPI.create(
        isFormData
          ? buildClientFormData(decamelizedClientParams)
          : decamelizedClientParams
      );

      AnalyticsHelper.track(CLIENTS_EVENTS.CREATE_CLIENT);
      commit(types.SET_CLIENT_ITEM, response.data.payload.client);
      commit(types.SET_CLIENT_UI_FLAG, { isCreating: false });
      return response.data.payload.client;
    } catch (error) {
      commit(types.SET_CLIENT_UI_FLAG, { isCreating: false });
      return handleClientOperationErrors(error);
    }
  },

  import: async ({ commit }, file) => {
    commit(types.SET_CLIENT_UI_FLAG, { isImporting: true });
    try {
      await ClientAPI.importClients(file);
      commit(types.SET_CLIENT_UI_FLAG, { isImporting: false });
    } catch (error) {
      commit(types.SET_CLIENT_UI_FLAG, { isImporting: false });
      if (error.response?.data?.message) {
        throw new ExceptionWithMessage(error.response.data.message);
      }
    }
  },

  export: async ({ commit }, { payload, label }) => {
    commit(types.SET_CLIENT_UI_FLAG, { isExporting: true });
    try {
      await ClientAPI.exportClients({ payload, label });

      commit(types.SET_CLIENT_UI_FLAG, { isExporting: false });
    } catch (error) {
      commit(types.SET_CLIENT_UI_FLAG, { isExporting: false });
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else {
        throw new Error(error);
      }
    }
  },

  delete: async ({ commit }, id) => {
    commit(types.SET_CLIENT_UI_FLAG, { isDeleting: true });
    try {
      await ClientAPI.delete(id);
      commit(types.SET_CLIENT_UI_FLAG, { isDeleting: false });
    } catch (error) {
      commit(types.SET_CLIENT_UI_FLAG, { isDeleting: false });
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else {
        throw new Error(error);
      }
    }
  },

  deleteCustomAttributes: async ({ commit }, { id, customAttributes }) => {
    try {
      const response = await ClientAPI.destroyCustomAttributes(
        id,
        customAttributes
      );
      commit(types.EDIT_CLIENT, response.data.payload);
    } catch (error) {
      throw new Error(error);
    }
  },

  deleteAvatar: async ({ commit }, id) => {
    try {
      const response = await ClientAPI.destroyAvatar(id);
      commit(types.EDIT_CLIENT, response.data.payload);
    } catch (error) {
      throw new Error(error);
    }
  },

  fetchClientableInbox: async ({ commit }, id) => {
    commit(types.SET_CLIENT_UI_FLAG, { isFetchingInboxes: true });
    try {
      const response = await ClientAPI.getClientableInboxes(id);
      const client = {
        id: Number(id),
        client_inboxes: response.data.payload,
      };
      commit(types.SET_CLIENT_ITEM, client);
    } catch (error) {
      if (error.response?.data?.message) {
        throw new ExceptionWithMessage(error.response.data.message);
      } else {
        throw new Error(error);
      }
    } finally {
      commit(types.SET_CLIENT_UI_FLAG, { isFetchingInboxes: false });
    }
  },

  updatePresence: ({ commit }, data) => {
    commit(types.UPDATE_CLIENTS_PRESENCE, data);
  },

  setClient({ commit }, data) {
    commit(types.SET_CLIENT_ITEM, data);
  },

  merge: async ({ commit }, { childId, parentId }) => {
    commit(types.SET_CLIENT_UI_FLAG, { isMerging: true });
    try {
      const response = await AccountActionsAPI.merge(parentId, childId);
      commit(types.SET_CLIENT_ITEM, response.data);
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_CLIENT_UI_FLAG, { isMerging: false });
    }
  },

  deleteClientThroughConversations: ({ commit }, id) => {
    commit(types.DELETE_CLIENT, id);
    commit(types.CLEAR_CLIENT_CONVERSATIONS, id, { root: true });
    commit(`clientConversations/${types.DELETE_CLIENT_CONVERSATION}`, id, {
      root: true,
    });
  },

  updateClient: async ({ commit }, updateObj) => {
    commit(types.SET_CLIENT_UI_FLAG, { isUpdating: true });
    try {
      commit(types.EDIT_CLIENT, updateObj);
      commit(types.SET_CLIENT_UI_FLAG, { isUpdating: false });
    } catch (error) {
      commit(types.SET_CLIENT_UI_FLAG, { isUpdating: false });
    }
  },

  filter: async (
    { commit },
    { page = 1, sortAttr, queryPayload, resetState = true } = {}
  ) => {
    commit(types.SET_CLIENT_UI_FLAG, { isFetching: true });
    try {
      const {
        data: { payload, meta },
      } = await ClientAPI.filter(page, sortAttr, queryPayload);
      if (resetState) {
        commit(types.CLEAR_CLIENTS);
        commit(types.SET_CLIENTS, payload);
        commit(types.SET_CLIENT_META, meta);
        commit(types.SET_CLIENT_UI_FLAG, { isFetching: false });
      }
      return payload;
    } catch (error) {
      commit(types.SET_CLIENT_UI_FLAG, { isFetching: false });
    }
    return [];
  },

  setClientFilters({ commit }, data) {
    commit(types.SET_CLIENT_FILTERS, data);
  },

  clearClientFilters({ commit }) {
    commit(types.CLEAR_CLIENT_FILTERS);
  },
};
